/**
 * Sheet 32, the transmission log (mission 7 WP11 T057; FR-014, FR-028,
 * NFR-007, NFR-008; TX.6, TX.8, D.2, D.5, UC-06, X.10, GEN.4, GEN.6, NF.7;
 * C-004 (A.3 as amended), C-008; analysis I1; squad U10, U11; research §1.15,
 * §1.19, §1.23; contracts/ui-api.md "Sheet 32").
 *
 * One default export for two routes, told apart by `useParams().runId`:
 *
 * - **Tenant-wide**, `/app/transmissions` (32/1, 32/3): `transmissionsQuery`
 *   over the filters held in the URL search params (`run`, `bureau`, `result`,
 *   `from`, `to`, `q`). A value the API would refuse (an unknown bureau or
 *   result, a run that is not a UUID, a date that is not `YYYY-MM-DD`, a `q`
 *   over 64 characters) is dropped from the URL with replace, as mission 6's
 *   history does. `bureausQuery` supplies the kill switch's `disabledSuffix`.
 *   `q` reaches only the read's query string.
 * - **Per run**, `/app/runs/:runId/transmissions` (32/12): `runTransmissionsQuery`,
 *   the run filter fixed and hidden. That read takes no other filter, so the
 *   page offers none. It shows `notSentTo` or `notSentToNone` and the
 *   `allTransmissions` link, the tenant log's entry point (U11). When
 *   `transmit_eligible` is false it renders the sentence
 *   `transmit_ineligible_reason` names, the past rows if any, and no not-sent
 *   line (analysis I1): X.10 `pathBNotTransmissible`, GEN.4
 *   `runNotReady({state: run_state})`, GEN.6 the NOT DELIVERABLE badge with
 *   `transmitNotDeliverable`. A Path A run never reads the Path B sentence.
 *
 * Both page 50 rows at a time along `next_cursor` (the cursor stack is
 * component state, never the URL or a key of its own), both poll every 2 s
 * while a visible row is pending and stop when none is (the WP07 queries;
 * NFR-008, U10), and both state the Class 2 retention (`class2Retention`,
 * 32/11). A failed poll keeps the rows and adds the generic band.
 */
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { Band } from '@/components/trellis/Band'
import { ProblemBand } from '@/components/trellis/ProblemBand'
import { Badge } from '@/components/ui/badge'
import { ApiError, InvalidIdError, idSegment } from '@/lib/api'
import { BUREAUS, type Bureau } from '@/lib/api/bureaus'
import {
  TRANSMISSION_RESULTS,
  type RunTransmissions,
  type TransmissionFilters,
  type TransmissionResult,
} from '@/lib/api/transmissions'
import { Mono, shortId } from '@/lib/format'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'
import { bureausQuery, runTransmissionsQuery, transmissionsQuery } from '@/lib/queries'

import { LogFilters, type FilterKey } from './LogFilters'
import { LogTable, type LogPaging } from './LogTable'

const FILTER_KEYS: readonly FilterKey[] = ['run', 'bureau', 'result', 'from', 'to', 'q']
const DAY = /^\d{4}-\d{2}-\d{2}$/
const Q_MAX = 64

interface Page {
  cursor: string
  /** Rows before this page, for the footer range. */
  offset: number
}

function isRunId(value: string): boolean {
  try {
    idSegment(value, 'run id')
    return true
  } catch {
    return false
  }
}

/** The filters the URL holds, and the keys whose value the API would refuse. */
function parseFilters(params: URLSearchParams): { filters: TransmissionFilters; bad: FilterKey[] } {
  const filters: TransmissionFilters = {}
  const bad: FilterKey[] = []
  const take = (key: FilterKey, ok: (v: string) => boolean, set: (v: string) => void) => {
    const raw = params.get(key)
    if (raw === null) return
    if (ok(raw)) set(raw)
    else bad.push(key)
  }
  take('run', isRunId, (v) => (filters.run = v))
  take('bureau', (v) => (BUREAUS as readonly string[]).includes(v), (v) => (filters.bureau = v as Bureau))
  take('result', (v) => (TRANSMISSION_RESULTS as readonly string[]).includes(v), (v) => (filters.result = v as TransmissionResult))
  take('from', (v) => DAY.test(v), (v) => (filters.from = v))
  take('to', (v) => DAY.test(v), (v) => (filters.to = v))
  take('q', (v) => v.length > 0 && v.length <= Q_MAX, (v) => (filters.q = v))
  return { filters, bad }
}

/** A cursor stack reset whenever `scope` changes (a filter, or the run). */
function useCursorStack(scope: string) {
  const [state, setState] = useState<{ scope: string; stack: Page[] }>({ scope, stack: [] })
  const stack = state.scope === scope ? state.stack : []
  const page = stack.at(-1)
  return {
    cursor: page?.cursor,
    paging: (rows: number, nextCursor: string | null | undefined): LogPaging => ({
      offset: page?.offset ?? 0,
      hasPrevious: stack.length > 0,
      hasNext: typeof nextCursor === 'string',
      onPrevious: () => setState({ scope, stack: stack.slice(0, -1) }),
      onNext: () => {
        if (typeof nextCursor === 'string') {
          setState({ scope, stack: [...stack, { cursor: nextCursor, offset: (page?.offset ?? 0) + rows }] })
        }
      },
    }),
  }
}

function ReadFailure({ error }: { error: unknown }) {
  const notFound = error instanceof InvalidIdError || (error instanceof ApiError && error.problem.status === 404)
  if (notFound) return <Band kind="info">{copyText('notFound')}</Band>
  if (error instanceof ApiError) return <ProblemBand problem={error.problem} />
  return <Band kind="blocked">{copyText('genericError')}</Band>
}

/** A poll that failed after rows were shown: the rows stay (U10), the band says so. */
function PollFailure() {
  return <Band kind="info">{copyText('genericError')}</Band>
}

function Retention() {
  return <p className="text-body-sm text-navy-600">{copyText('class2Retention')}</p>
}

function TenantLog() {
  const [params, setParams] = useSearchParams()
  const { filters, bad } = parseFilters(params)
  const badKey = bad.join(',')

  useEffect(() => {
    if (badKey === '') return
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const key of badKey.split(',')) next.delete(key)
        return next
      },
      { replace: true },
    )
  }, [badKey, setParams])

  const scope = FILTER_KEYS.map((k) => filters[k] ?? '').join('|')
  const { cursor, paging } = useCursorStack(scope)
  const log = useQuery(transmissionsQuery(filters, cursor))
  const bureaus = useQuery(bureausQuery())
  const disabled = new Set((bureaus.data?.bureaus ?? []).filter((b) => b.disabled_by_trellis).map((b) => b.bureau))

  function setFilter(key: FilterKey, value: string | undefined) {
    setParams((prev) => {
      const next = new URLSearchParams(prev)
      if (value === undefined) next.delete(key)
      else next.set(key, value)
      return next
    })
  }

  const filtered = FILTER_KEYS.some((k) => filters[k] !== undefined)
  const settled = log.data !== undefined && !log.isPlaceholderData

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-h1 text-navy-950">{copyText('crumb.transmissions')}</h1>
      <Retention />
      <LogFilters filters={filters} disabled={disabled} onChange={setFilter} />
      {log.data === undefined && log.isError ? (
        <ReadFailure error={log.error} />
      ) : (
        <>
          {log.isError ? <PollFailure /> : null}
          <LogTable
            entries={settled ? log.data.entries : []}
            loading={!settled}
            emptyText={copyText(filtered ? 'logEmptyFiltered' : 'logEmpty')}
            paging={paging(settled ? log.data.entries.length : 0, settled ? log.data.next_cursor : null)}
          />
        </>
      )}
    </div>
  )
}

/** The per-run line under the title: not sent to, or why the run can never be transmitted (I1). */
function RunLine({ body }: { body: RunTransmissions }) {
  if (body.transmit_eligible) {
    return (
      <p className="text-body text-navy-950">
        {body.not_sent_to.length > 0
          ? PROVISIONAL_COPY.notSentTo.render({ bureaus: body.not_sent_to })
          : copyText('notSentToNone')}
      </p>
    )
  }
  switch (body.transmit_ineligible_reason) {
    case 'X.10':
      return (
        <Band kind="info" requirement="X.10">
          <p>{copyText('pathBNotTransmissible')}</p>
        </Band>
      )
    case 'GEN.4':
      return (
        <Band kind="info" requirement="GEN.4">
          <p>{PROVISIONAL_COPY.runNotReady.render(body.run_state === undefined ? {} : { state: body.run_state })}</p>
        </Band>
      )
    case 'GEN.6':
      return (
        <Band kind="info" requirement="GEN.6">
          <p className="flex flex-wrap items-center gap-2">
            {/* mission 7 WP14: J3, the badge text is a register entry (P22). */}
            <Badge status="not-deliverable">{copyText('notDeliverableBadge')}</Badge>
            <span>{copyText('transmitNotDeliverable')}</span>
          </p>
        </Band>
      )
    default:
      return null
  }
}

function RunLog({ runId }: { runId: string }) {
  const { cursor, paging } = useCursorStack(runId)
  const log = useQuery(runTransmissionsQuery(runId, cursor))
  const label = PROVISIONAL_COPY.runLabel.render({ runId })
  const id = shortId(runId)
  const at = label.indexOf(id)

  if (log.data === undefined && log.isError) return <ReadFailure error={log.error} />
  const settled = log.data !== undefined && !log.isPlaceholderData

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-4">
        <h1 className="text-h1 text-navy-950">
          {copyText('crumb.transmissions')} ·{' '}
          {at < 0 ? (
            label
          ) : (
            <>
              {label.slice(0, at)}
              <Mono>{id}</Mono>
              {label.slice(at + id.length)}
            </>
          )}
        </h1>
        <Link
          to="/app/transmissions"
          className="ml-auto text-body text-[var(--color-link)] underline-offset-4 hover:text-[var(--color-link-hover)] hover:underline"
        >
          {copyText('allTransmissions')}
        </Link>
      </div>
      {log.data !== undefined ? <RunLine body={log.data} /> : null}
      <Retention />
      {log.isError ? <PollFailure /> : null}
      <LogTable
        entries={settled ? log.data.entries : []}
        loading={!settled}
        emptyText={copyText('logEmpty')}
        paging={paging(settled ? log.data.entries.length : 0, settled ? log.data.next_cursor : null)}
      />
    </div>
  )
}

export default function TransmissionLog() {
  const { runId } = useParams()
  return runId === undefined ? <TenantLog /> : <RunLog runId={runId} />
}
